// 261010 UI1c — plan NHIỀU video: dòng tóm tắt mỗi video, cảnh thu/mở, bộ lọc mặc định "Cần xử lý" (THUẦN, không antd).
// "Cần xử lý" = lỗi + thiếu (chưa có kết quả) + cũ — chỉ ở bước cấp VIDEO (SCRIPT / ASSETS dùng một trạng thái chung cho cả plan ⇒ không có việc theo từng video).
import type { FilmEvent, PositionMapsResponse, Storyboard } from '../../types/film';
import type { StepRunObject, WorkflowEventStatus, WorkflowStepKey, WorkflowStepState, WorkflowStepSummary } from '../../types/filmWorkflow';
import { eventFlagInfosOf } from './workflowFlagUtils';
import { EVENT_STEP_KEYS, formatClock, stateOfEvent, type SceneGroup, type StateFilter } from './workflowBoardUtils';
import { mapsOfEvent } from './workflowDetailUtils';
import { fmtSec, shotSizeEn } from './shotCameraUtils';
import { thumbsOfStep } from './workflowThumbsUtils';

export type AttnKind = 'ERROR' | 'MISSING' | 'STALE';
export interface AttnCounts { total: number; error: number; missing: number; stale: number; ids: string[] }
/** `flags` = cần chú ý / lỗi · `kept` = thông tin trung tính (giữ bản sửa tay, máy không ghi đè). */
export interface RowSummary { headline: string; meta: string[]; thumbs: string[]; flags: string[]; kept?: string[] }
export interface RowCtx {
  event: WorkflowEventStatus;
  /** Chi tiết video (từ `GET /events`); chưa nạp xong ⇒ null / vắng. */
  ev?: FilmEvent | null;
  /** Số cảnh báo của video ở bước này (từ `workflow-status.alerts`). */
  alertCount?: number;
  /** `undefined` = chưa nạp; `null` = đã nạp mà video chưa có storyboard. */
  storyboard?: Storyboard | null;
  maps?: PositionMapsResponse | null;
  /** `workflow-status.stepRuns[bước].objects` — để biết nguyên nhân thật của cờ BLOCKED_* (stepFlags chỉ mang mã). */
  runObjects?: StepRunObject[];
}

/** L2: video vượt trần giây của engine (`generation.block`) ⇒ máy TỰ CHIA ở bước Đạo diễn (giữ trọn chữ). */
export function needsSplitOf(ev: Pick<FilmEvent, 'generation'> | null | undefined): { rawS: number; capS: number } | null {
  const b = ev?.generation?.block;
  return b && b.code === 'NEEDS_SPLIT' ? { rawS: b.rawS, capS: b.capS } : null;
}
export const splitNoteOf = (b: { rawS: number; capS: number }): string =>
  `Video ước ${(Math.round(b.rawS * 10) / 10).toString().replace('.', ',')} s, vượt trần ${b.capS} s của engine — máy tự chia ở bước Đạo diễn (giữ trọn chữ, không cắt giữa câu).`;

export const attentionOf = (s: WorkflowStepState): AttnKind | null => (s === 'ERROR' ? 'ERROR' : s === 'NOT_READY' ? 'MISSING' : s === 'STALE' ? 'STALE' : null);

const isEventStep = (k: WorkflowStepKey): boolean => (EVENT_STEP_KEYS as string[]).includes(k);

export function attentionCountsOf(events: WorkflowEventStatus[], step: WorkflowStepKey, planSteps: WorkflowStepSummary[]): AttnCounts {
  const out: AttnCounts = { total: 0, error: 0, missing: 0, stale: 0, ids: [] };
  if (!isEventStep(step)) return out;
  for (const e of events ?? []) {
    const a = attentionOf(stateOfEvent(e, step, planSteps));
    if (!a) continue;
    out.total += 1; out.ids.push(e.eventId);
    if (a === 'ERROR') out.error += 1; else if (a === 'MISSING') out.missing += 1; else out.stale += 1;
  }
  return out;
}

/** `AUTO` (chưa chọn) ⇒ "Cần xử lý" nếu có việc, ngược lại "Tất cả". Lựa chọn rõ ràng của người dùng giữ nguyên. */
export const resolveStateFilter = (sel: StateFilter, attn: number): StateFilter => (sel === 'AUTO' ? (attn > 0 ? 'ATTN' : 'ALL') : sel);

/** Cảnh nào mở mặc định: có việc cần xử lý ⇒ chỉ cảnh có việc; không có việc (hoặc bước cấp plan) ⇒ mở hết. */
export function defaultOpenScenesOf(groups: SceneGroup[], step: WorkflowStepKey, planSteps: WorkflowStepSummary[]): Record<string, boolean> {
  const all = (groups ?? []).flatMap((g) => g.events);
  const hasAttn = attentionCountsOf(all, step, planSteps).total > 0;
  const out: Record<string, boolean> = {};
  for (const g of groups ?? []) out[g.sceneId] = !hasAttn || attentionCountsOf(g.events, step, planSteps).total > 0;
  return out;
}

/** "Cảnh N · x video · y lỗi · z thiếu · w cũ" (bỏ phần bằng 0). */
export function sceneLineOf(g: SceneGroup, step: WorkflowStepKey, planSteps: WorkflowStepSummary[]): string {
  const c = attentionCountsOf(g.events, step, planSteps);
  return [`Cảnh ${g.sceneOrder}`, `${g.events.length} video`, ...(c.error ? [`${c.error} lỗi`] : []), ...(c.missing ? [`${c.missing} thiếu`] : []), ...(c.stale ? [`${c.stale} cũ`] : [])].join(' · ');
}

/** Cỡ cảnh xuất hiện nhiều nhất; hoà ⇒ cỡ xuất hiện trước. */
export function mainShotSizeOf(shots: Array<{ shotSize?: string | null }> | null | undefined): string | null {
  const count = new Map<string, number>();
  for (const s of shots ?? []) { const k = String(s.shotSize ?? '').trim().toUpperCase(); if (k) count.set(k, (count.get(k) ?? 0) + 1); }
  let best: string | null = null; let n = 0;
  count.forEach((v, k) => { if (v > n) { best = k; n = v; } });
  return best;
}

const clip = (t: string, max: number): string => (t.length > max ? `${t.slice(0, max - 1)}…` : t);
const firstSentence = (t: string, max: number): string => {
  const s = t.replace(/\s+/g, ' ').trim();
  const m = /^.*?[.!?](?=\s|$)/.exec(s);
  return clip(m ? m[0] : s, max);
};

export function rowSummaryOf(step: WorkflowStepKey, c: RowCtx): RowSummary {
  // Cờ chung mọi bước: cảnh báo · cờ lỗi bền của CHÍNH bước này (`events[].stepFlags[step]`) · NEEDS_SPLIT (L2). Không lặp.
  const infos = eventFlagInfosOf(step, c.event, c.runObjects);
  const own = infos.filter((i) => i.kind === 'ERROR').map((i) => (i.label === 'lỗi' ? 'lỗi chạy' : i.label));
  const kept = Array.from(new Set(infos.filter((i) => i.kind === 'KEPT').map((i) => i.label)));
  const split = needsSplitOf(c.ev) ? ['cần chia'] : [];
  const common = Array.from(new Set([...own, ...split]));
  const base = (headline: string, meta: string[] = [], thumbs: string[] = [], flags: string[] = []): RowSummary => ({
    headline, meta, thumbs, flags: [...((c.alertCount ?? 0) > 0 ? [`${c.alertCount} cảnh báo`] : []), ...common, ...flags], ...(kept.length ? { kept } : {}),
  });
  const ev = c.ev;
  if (!ev) return base('Đang tải chi tiết…');
  const shots = ev.shots ?? [];
  const phases = ev.videoMeta?.phases ?? [];
  switch (step) {
    case 'SCRIPT': {
      const action = String(ev.action ?? '').trim();
      return base(action ? firstSentence(action, 140) : 'Chưa có mô tả hành động.', [formatClock(ev.genDurationS), `${(ev.dialogueLines ?? []).length} thoại`, `${phases.length} giai đoạn`]);
    }
    case 'DIRECTING': {
      const cast = Array.from(new Set(shots.flatMap((s) => s.characterNames ?? [])));
      return base(shots.length ? `${shots.length} shot` : 'Chưa có shot nào.', cast.length ? [cast.join(', ')] : []);
    }
    case 'CINEMATOGRAPHY': {
      if (!shots.length) return base('Chưa có shot nào.');
      const main = mainShotSizeOf(shots);
      const total = shots.reduce((n, s) => n + (typeof s.durationS === 'number' ? s.durationS : 0), 0);
      return base(`${shots.length} shot${main ? ` · cỡ chính ${main} (${shotSizeEn(main)})` : ''}`, total > 0 ? [`${fmtSec(total)} s`] : []);
    }
    case 'MAP': {
      if (!c.maps) return base('Đang tải sơ đồ…');
      const rows = mapsOfEvent(c.maps, c.event.eventId, phases.map((p) => p.order));
      const linked = rows.filter((r) => r.map);
      const missing = rows.length - linked.length;
      return base(`${linked.length}/${rows.length} giai đoạn có sơ đồ`, [], thumbsOfStep('MAP', { eventId: c.event.eventId, phaseOrders: phases.map((p) => p.order), maps: c.maps }), missing > 0 ? [`thiếu ${missing} sơ đồ`] : []);
    }
    case 'STORYBOARD': {
      if (c.storyboard === undefined) return base('Đang tải storyboard…');
      const sb = c.storyboard;
      if (!sb) return base('Chưa có storyboard.');
      const thumbs = thumbsOfStep('STORYBOARD', { eventId: c.event.eventId, phaseOrders: phases.map((p) => p.order), storyboard: sb });
      return base(`${sb.frames?.length ?? 0} khung · ${sb.groups?.length ?? 0} nhóm`, [], thumbs, (sb.drift?.length ?? 0) > 0 ? [`${sb.drift.length} khung lệch`] : []);
    }
    case 'PROMPT': {
      const p = String(ev.authorPrompt ?? '').trim();
      if (!p) return base('Chưa có prompt.');
      const mode = ev.effectivePromptMode === 'NATURAL' ? 'Tự nhiên' : ev.effectivePromptMode === 'TECHNICAL' ? 'Kỹ thuật' : '';
      const w = (ev.promptWarnings ?? []).filter(Boolean).length;
      return base(clip(p.replace(/\s+/g, ' '), 120), [`${p.length} ký tự`, ...(mode ? [mode] : [])], [], [...(w ? [`${w} cảnh báo prompt`] : []), ...(ev.promptModeState === 'MISMATCH' ? ['lệch kiểu'] : [])]);
    }
    default:
      return base(`${(ev.clips ?? []).length} bản clip`);
  }
}
